import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { EDITIONS, bookPriceForUser } from "../data/content";
import { Ornament } from "../components/Ornament";
import { LanguageSelector } from "../components/LanguageSelector";
import { SUPPORTED_LANGUAGES } from "../i18n";

export default function OrderPage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [me, setMe] = useState(null);
  const [edition, setEdition] = useState("hardcover");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Translate-before-print state
  const [translateLang, setTranslateLang] = useState("");  // "" = print in original
  const [translating, setTranslating] = useState(false);
  const [translatedFor, setTranslatedFor] = useState(null); // language code we've translated to

  useEffect(() => {
    (async () => {
      try {
        const [plist, meRes] = await Promise.all([
          api.get("/profiles"),
          api.get("/auth/me"),
        ]);
        const p = (plist.data || []).find((p) => p.profile_id === profileId);
        if (!p) { navigate("/dashboard"); return; }
        setProfile(p);
        setMe(meRes.data);
      } catch (e) { navigate("/dashboard"); }
    })();
  }, [profileId, navigate]);

  const checkout = async () => {
    setBusy(true); setError("");
    try {
      const res = await api.post("/payments/checkout", { profile_id: profileId, edition, origin_url: window.location.origin });
      if (res.data?.url) window.location.href = res.data.url;
      else setError("Could not start checkout.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const runTranslate = async () => {
    if (!translateLang) { setTranslatedFor(null); return; }
    setTranslating(true); setError("");
    try {
      await api.post("/translate/book", { profile_id: profileId, target_language: translateLang });
      setTranslatedFor(translateLang);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Translation failed."));
    } finally { setTranslating(false); }
  };

  if (!profile || !me) return (
    <div className="min-h-screen flex items-center justify-center paper">
      <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>{t("common.loading")}</p>
    </div>
  );

  const tier = me.subscription_tier || "free";
  const selected = EDITIONS.find((e) => e.id === edition);
  const sel = bookPriceForUser(selected, me);

  return (
    <div className="min-h-screen paper" data-testid="order-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to={`/app/profile/${profileId}`} className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-link">
          <ChevronLeft size={16} /> {t("common.back")}
        </Link>
        <div className="flex items-center gap-5">
          {tier === "free" && (
            <Link to="/subscribe" className="label-ui text-[11px] text-[#B8973A] hover:text-[#9d8030] transition-colors" data-testid="upgrade-for-discount">
              {t("order.subToSave")} →
            </Link>
          )}
          {false && <LanguageSelector tone="light" />}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <div className="text-center mb-16">
          <p className="label-ui text-[11px] text-[#B8973A]">{t("order.orderTitle", { name: profile.name })}</p>
          <h1 className="text-4xl sm:text-5xl mt-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {t("order.chooseEdition1")} <span style={{ fontStyle: "italic" }}>{t("order.chooseEdition2")}</span>
          </h1>
          <Ornament className="my-8" />
          <p className="italic text-[#8C8278] max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
            {t("order.subSaveHint")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {EDITIONS.map((e) => {
            const active = edition === e.id;
            const eP = bookPriceForUser(e, me);
            const hasDiscount = eP.discount > 0;
            return (
              <button
                key={e.id} type="button" onClick={() => setEdition(e.id)}
                className={`text-left p-8 transition-all duration-500 hairline-cream relative ${active ? "bg-white" : "bg-[#FAF7F2] hover:bg-white"}`}
                style={{ borderRadius: 4, borderColor: active ? "#B8973A" : undefined, borderWidth: active ? 1 : undefined }}
                data-testid={`edition-${e.id}`}
              >
                {e.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 label-ui text-[10px]" style={{ background: "#B8973A", color: "#FAF7F2", borderRadius: 2 }}>{e.badge}</div>
                )}
                <p className="chapter-roman mb-4">{e.id === "softcover" ? "I" : e.id === "hardcover" ? "II" : "III"}</p>
                <h3 className="text-2xl mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{e.name}</h3>
                <p className="text-[#8C8278] text-sm mb-4">{e.size}</p>
                {hasDiscount ? (
                  <div className="mb-2">
                    <span className="text-3xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>£{eP.price}</span>
                    <span className="text-[#8C8278] line-through text-base ml-3">£{eP.base}</span>
                  </div>
                ) : (
                  <div className="text-3xl mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>£{eP.price}</div>
                )}
                {hasDiscount && (
                  <p className="label-ui text-[10px] text-[#B8973A] mb-3">{eP.label}</p>
                )}
                {!hasDiscount && tier === "free" && (
                  <p className="label-ui text-[10px] text-[#8C8278] mb-3">{t("order.subToSave")}</p>
                )}
                <p className="text-[#8C8278] text-sm leading-relaxed mb-5">{t(`order.${e.id}Blurb`, e.blurb)}</p>
                <p className="label-ui text-[10px] text-[#B8973A]">{active ? t("common.selected") : t("common.choose")}</p>
              </button>
            );
          })}
        </div>

        {/* Translate-before-print picker (subscribers only) */}
        {tier !== "free" && (
          <div className="bg-[#F7F0E0] hairline-cream p-6 sm:p-7 max-w-2xl mx-auto mb-6" style={{ borderRadius: 4 }} data-testid="translate-card">
            <p className="label-ui text-[10px] text-[#B8973A] mb-2 flex items-center gap-2">
              <Languages size={12} /> {t("order.translateBook")}
            </p>
            <p className="text-[#8C8278] italic text-sm mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
              {t("order.translateBookHelp")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={translateLang}
                onChange={(e) => setTranslateLang(e.target.value)}
                className="flex-1 px-4 py-3 hairline-cream bg-white"
                style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", minHeight: 48 }}
                data-testid="translate-language-select"
              >
                <option value="">{t("order.translateLeaveOriginal")}</option>
                {SUPPORTED_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={runTranslate}
                disabled={!translateLang || translating}
                className="btn-outline-navy flex items-center justify-center gap-2 px-6"
                data-testid="translate-run-btn"
              >
                {translating ? <Loader2 size={14} className="animate-spin" /> : null}
                {translating ? t("order.translating") : t("order.translateBook")}
              </button>
            </div>
            {translatedFor && !translating && (
              <p className="label-ui text-[10px] text-[#B8973A] mt-3" data-testid="translate-done-msg">
                ✦ {t("order.translateDone")} — {SUPPORTED_LANGUAGES.find((l) => l.code === translatedFor)?.label}
              </p>
            )}
          </div>
        )}

        <div className="bg-white hairline-cream p-8 sm:p-10 max-w-2xl mx-auto text-center" style={{ borderRadius: 4 }}>
          <p className="label-ui text-[11px] text-[#B8973A] mb-3">{t("order.yourSelection")}</p>
          <h2 className="text-3xl mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{selected.name}</h2>
          <p className="text-[#8C8278] text-sm mb-6">{selected.size}</p>
          {sel.discount > 0 ? (
            <div className="mb-8">
              <div className="text-4xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                £{sel.price}
                <span className="text-[#8C8278] line-through text-xl ml-4">£{sel.base}</span>
              </div>
              <p className="label-ui text-[10px] text-[#B8973A] mt-2">{t("order.discountApplied", { label: sel.label })}</p>
            </div>
          ) : (
            <div className="text-4xl mb-8" style={{ fontFamily: "Cormorant Garamond, serif" }}>£{sel.price}</div>
          )}
          {error && <p className="text-red-700 text-sm mb-4" data-testid="order-error">{error}</p>}
          <button onClick={checkout} disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="checkout-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            {busy ? t("order.preparing") : t("order.checkoutBtn")}
          </button>
          <p className="text-xs text-[#8C8278] italic mt-4" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1rem" }}>
            {t("order.deliveryNote")}
          </p>
          <p className="mt-6 text-[#111D33] italic max-w-md mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.4 }} data-testid="order-manifesto">
            "The conversation is the gift. The book is simply where it comes to rest."
          </p>
        </div>
      </main>
    </div>
  );
}
